import { useQuery, useMutation } from '@tanstack/react-query'
import { modelsApi } from '@/services/api/models'
import type { ModelFilters } from '@/types/models'
import { buildApiUrl } from '@/utils/api'
import { useAuth } from './useAuth'

// Get models list
export function useModels(filters?: ModelFilters) {
  return useQuery({
    queryKey: ['models', filters],
    queryFn: () => modelsApi.getModels(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Get single model
export function useModel(id: string | null) {
  return useQuery({
    queryKey: ['model', id],
    queryFn: () => modelsApi.getModel(id!),
    enabled: !!id,
  })
}

// Get categories
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => modelsApi.getCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Record model view
export function useRecordView() {
  return useMutation({
    mutationFn: ({ modelId, duration }: { modelId: string; duration?: number }) =>
      modelsApi.recordView(modelId, duration),
  })
}

// Search models
export function useModelSearch(query: string) {
  return useQuery({
    queryKey: ['models', 'search', query],
    queryFn: () => modelsApi.searchModels(query),
    enabled: query.length >= 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// Get featured model for homepage
export function useFeaturedModel() {
  return useQuery({
    queryKey: ['featured-model'],
    queryFn: async () => {
      const response = await fetch(buildApiUrl('/api/v2/models/featured'))
      if (!response.ok) {
        throw new Error('Failed to fetch featured model')
      }
      const data = await response.json()
      return data.data
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  })
}

// Get single model for admin (includes unlisted models)
export function useAdminModel(id: string | null) {
  const { token } = useAuth()
  
  return useQuery({
    queryKey: ['admin-model', id],
    queryFn: async () => {
      const response = await fetch(buildApiUrl(`/api/v2/admin/models/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': localStorage.getItem('adminEmail') || '',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch model: ${response.status}`)
      }
      
      return response.json()
    },
    enabled: !!id && !!token,
  })
}